import { useState, useEffect, useMemo, useCallback } from "react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { CalendarBlank, CaretLeft, CaretRight, Briefcase, Clock, MapPin, User, Lightning } from "@phosphor-icons/react";
import AssigneeChip from "../components/AssigneeChip";
import ComplianceBadge from "../components/ComplianceBadge";
import useComplianceBadges from "../hooks/useComplianceBadges";
import { todayLocalISO } from "../lib/dateUtil";
import { computeRowBars, rowGridTemplateRows, BAR_HEIGHT_PX } from "../lib/calendarLayout";
import QuickCompleteButton from "../components/QuickCompleteButton";
import { useAuth } from "../contexts/AuthContext";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Phase 270 — Unified Type/Status colour system (matches DashboardCalendar)
const TYPE_TINT = {
  emergency: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
  job: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  visit: "bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300",
  task: "bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300",
};
const isCompletedStatus = (s) => ["completed", "approved", "completed_by_vendor", "resolved", "closed"].includes(s);
const isInProgressStatus = (s) => ["in_progress", "awaiting_materials", "awaiting_review", "acknowledged"].includes(s);
function getDotColor(status, effectiveEndDate, todayStr) {
  if (isCompletedStatus(status)) return "#10b981";
  if (effectiveEndDate && todayStr && effectiveEndDate < todayStr && !isCompletedStatus(status)) return "#dc2626";
  if (isInProgressStatus(status)) return "#ea580c";
  return "#1d4ed8";
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year, month) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1; // Monday-based
}

export default function JobCalendarPage() {
  const [jobs, setJobs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [emergencyJobs, setEmergencyJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());
  const [filter, setFilter] = useState("all"); // all, jobs, tasks, emergency
  // Phase 282 — View mode toggle (month / week / day)
  const [viewMode, setViewMode] = useState("month");
  // Phase 284 — Day-view sub-mode (list / timeline)
  const [dayMode, setDayMode] = useState("list");
  // For week/day modes we track a single anchor date
  const [anchorDate, setAnchorDate] = useState(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
  });

  const { user } = useAuth();
  const canComplete = ["super_admin", "admin", "project_manager"].includes(user?.role);
  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      API.get("/jobs?limit=500&include_contract_visits=true").then(({ data }) => setJobs(data.jobs || [])).catch(() => {}),
      API.get("/tasks?limit=500").then(({ data }) => setTasks(Array.isArray(data) ? data : (data.tasks || []))).catch(() => {}),
      API.get("/emergency-jobs?limit=500").then(({ data }) => setEmergencyJobs(Array.isArray(data) ? data : (data.emergency_jobs || data.jobs || []))).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const completeEntity = (e) => (e.type === "emergency" ? "emergency" : (e.type === "job" || e.type === "visit") ? "job" : null);

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };
  const goToday = () => {
    const t = new Date();
    setYear(t.getFullYear());
    setMonth(t.getMonth());
    setAnchorDate(`${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`);
  };

  // Phase 282 — Anchor-date navigation helpers (week / day views)
  const shiftAnchor = (days) => {
    const d = new Date(anchorDate + "T00:00:00");
    d.setDate(d.getDate() + days);
    setAnchorDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
  };
  const navPrev = () => {
    if (viewMode === "month") prevMonth();
    else if (viewMode === "week") shiftAnchor(-7);
    else shiftAnchor(-1);
  };
  const navNext = () => {
    if (viewMode === "month") nextMonth();
    else if (viewMode === "week") shiftAnchor(7);
    else shiftAnchor(1);
  };

  // Compute the week's Monday-anchored 7 dates for week-view
  const weekDates = (() => {
    if (viewMode !== "week") return [];
    const d = new Date(anchorDate + "T00:00:00");
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // back to Monday
    return Array.from({ length: 7 }, (_, i) => {
      const di = new Date(d); di.setDate(d.getDate() + i);
      return `${di.getFullYear()}-${String(di.getMonth() + 1).padStart(2, "0")}-${String(di.getDate()).padStart(2, "0")}`;
    });
  })();

  // Build calendar events from emergencies, jobs and tasks
  const events = useMemo(() => {
    const ev = [];
    if (filter === "all" || filter === "emergency") {
      for (const e of emergencyJobs) {
        const date = e.schedule?.start_date || e.created_at?.slice(0, 10);
        const assigned = e.assigned_vendor_name || e.assigned_operative_name || "";
        const assignedKind = e.assigned_vendor_name ? "supplier" : e.assigned_operative_name ? "operative" : "";
        if (date) ev.push({ id: e.id, date, endDate: e.schedule?.end_date || date, title: e.title, type: "emergency", status: e.status, ref: e.reference, vendor: assigned, assignedKind, time: e.schedule?.start_time, href: `/emergency-jobs/${e.id}` });
      }
    }
    if (filter === "all" || filter === "jobs") {
      for (const j of jobs) {
        const date = j.schedule?.start_date || j.created_at?.slice(0, 10);
        const assigned = j.assigned_vendor_name || j.assigned_operative_name || "";
        const assignedKind = j.assigned_vendor_name ? "supplier" : j.assigned_operative_name ? "operative" : "";
        if (date) ev.push({ id: j.id, date, endDate: j.schedule?.end_date || date, title: j.contract_id ? `↻ ${j.title}` : j.title, type: j.contract_id ? "visit" : "job", status: j.status, ref: j.reference, vendor: assigned, assignedKind, time: j.schedule?.start_time, address: `${j.street_address || ""} ${j.postcode || ""}`.trim(), href: `/jobs/${j.id}` });
      }
    }
    if (filter === "all" || filter === "tasks") {
      for (const t of tasks) {
        const primaryStart = t.start_date || t.due_date?.slice(0, 10) || t.created_at?.slice(0, 10);
        const primaryEnd = t.end_date || primaryStart;
        const assigned = t.assigned_to_name || t.vendor_name || "";
        const assignedKind = t.assigned_type === "vendor" ? "supplier" : t.assigned_type === "in_house" ? "operative" : (t.vendor_name ? "supplier" : (t.assigned_to_name ? "operative" : ""));
        if (primaryStart) ev.push({ id: t.id, date: primaryStart, endDate: primaryEnd, title: t.title, type: "task", status: t.status, ref: t.reference, vendor: assigned, assignedKind, time: t.start_time, href: t.project_id ? `/projects/${t.project_id}` : "/projects" });
        (t.scheduled_dates || []).forEach((s, sIdx) => {
          if (!s.start_date) return;
          ev.push({ id: `${t.id}__s${sIdx + 1}`, date: s.start_date, endDate: s.end_date || s.start_date, title: t.title, type: "task", status: t.status, ref: t.reference, vendor: assigned, assignedKind, time: s.start_time, href: t.project_id ? `/projects/${t.project_id}` : "/projects" });
        });
      }
    }
    return ev;
  }, [emergencyJobs, jobs, tasks, filter]);

  // Group events by date — multi-day items occupy every cell from start→end
  // (within the visible month). Each occupancy records bar position.
  const eventsByDate = useMemo(() => {
    const m = {};
    const monthStart = `${year}-${String(month + 1).padStart(2, "0")}-01`;
    const lastDay = getDaysInMonth(year, month);
    const monthEnd = `${year}-${String(month + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    for (const e of events) {
      const rawStart = e.date;
      const rawEnd = e.endDate || e.date;
      const start = rawStart < monthStart ? monthStart : rawStart;
      const end = rawEnd > monthEnd ? monthEnd : rawEnd;
      if (start > end) continue;
      const continuesLeft = rawStart < monthStart;
      const continuesRight = rawEnd > monthEnd;
      let cur = new Date(start + "T00:00:00");
      const stop = new Date(end + "T00:00:00");
      while (cur <= stop) {
        const ds = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(cur.getDate()).padStart(2, "0")}`;
        const isFirst = ds === start;
        const isLast = ds === end;
        const position = isFirst && isLast ? "single" : isFirst ? "start" : isLast ? "end" : "middle";
        if (!m[ds]) m[ds] = [];
        m[ds].push({ ...e, position, continuesLeft: isFirst && continuesLeft, continuesRight: isLast && continuesRight });
        cur.setDate(cur.getDate() + 1);
      }
    }
    return m;
  }, [events, year, month]);

  // Continuous-bar events — normalised for the calendarLayout helper.
  const barEvents = useMemo(() => events.map(e => ({
    id: e.id,
    startISO: e.date,
    endISO: e.endDate || e.date,
    startTime: e.time || "",
    ev: e,
  })), [events]);

  // Compliance ack summary — only meaningful for real "job" events (not
  // emergencies or tasks). We pass the deduped job ids; the badge renderer
  // ignores anything missing from the map.
  const jobEventIds = useMemo(
    () => Array.from(new Set(events.filter(e => e.type === "job").map(e => e.id))),
    [events],
  );
  const complianceByJob = useComplianceBadges(jobEventIds);

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);
  const today = todayLocalISO();

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="job-calendar-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
            <CalendarBlank size={18} className="text-white" weight="fill" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Schedule</h1>
            <p className="text-xs text-muted-foreground">{events.length} events this period</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Phase 282 — View mode toggle */}
          <div className="inline-flex rounded-md border border-border bg-card overflow-hidden text-[11px]" data-testid="calendar-view-toggle">
            {["month", "week", "day"].map(m => (
              <button
                key={m}
                type="button"
                onClick={() => setViewMode(m)}
                className={`px-3 py-1.5 capitalize font-medium transition ${viewMode === m ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                data-testid={`calendar-view-${m}`}
              >
                {m}
              </button>
            ))}
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-32 h-8 text-xs" data-testid="calendar-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="emergency">Emergencies</SelectItem>
              <SelectItem value="jobs">Jobs Only</SelectItem>
              <SelectItem value="tasks">Tasks Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Period Navigation (header label adapts to view mode) */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={navPrev} data-testid="prev-period"><CaretLeft size={16} /></Button>
          <h2 className="text-base font-semibold font-[Chivo] min-w-[260px] text-center" data-testid="calendar-period-label">
            {viewMode === "month" && `${MONTHS[month]} ${year}`}
            {viewMode === "week" && weekDates.length > 0 && (() => {
              const a = new Date(weekDates[0] + "T00:00:00");
              const b = new Date(weekDates[6] + "T00:00:00");
              const aFmt = a.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
              const bFmt = b.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
              return `${aFmt} — ${bFmt}`;
            })()}
            {viewMode === "day" && new Date(anchorDate + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </h2>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={navNext} data-testid="next-period"><CaretRight size={16} /></Button>
        </div>
        <Button variant="outline" size="sm" onClick={goToday} data-testid="today-btn">Today</Button>
      </div>

      {/* MONTH VIEW */}
      {viewMode === "month" && (() => {
        // Build all 42 cells (6 rows × 7 days) with real ISO dates — even spill
        // cells get a real date so multi-day bars wrap correctly across months.
        const cells = [];
        const prevDays = getDaysInMonth(year, month - 1);
        const prevY = month === 0 ? year - 1 : year;
        const prevM = month === 0 ? 11 : month - 1;
        for (let i = firstDay - 1; i >= 0; i--) {
          const d = prevDays - i;
          cells.push({ day: d, current: false, dateStr: `${prevY}-${String(prevM + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` });
        }
        for (let d = 1; d <= daysInMonth; d++) {
          const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          cells.push({ day: d, current: true, dateStr });
        }
        const nextY = month === 11 ? year + 1 : year;
        const nextM = month === 11 ? 0 : month + 1;
        let fill = 1;
        while (cells.length < 42) {
          cells.push({ day: fill, current: false, dateStr: `${nextY}-${String(nextM + 1).padStart(2, "0")}-${String(fill).padStart(2, "0")}` });
          fill++;
        }
        return (
        <div className="border border-border rounded-lg overflow-hidden bg-card" data-testid="calendar-month-view">
          {/* Day headers */}
          <div className="grid grid-cols-7 border-b border-border">
            {DAYS.map(d => (
              <div key={d} className="px-2 py-2 text-center text-[10px] font-semibold text-muted-foreground bg-muted/30">{d}</div>
            ))}
          </div>
          {/* Weekly rows with continuous multi-day bars */}
          {Array.from({ length: 6 }).map((_, rowIdx) => {
            const rowCells = cells.slice(rowIdx * 7, rowIdx * 7 + 7);
            const rowDays = rowCells.map(c => c.dateStr);
            const { bars, maxSlot } = computeRowBars(rowDays, barEvents);
            const MAX_VISIBLE = 6;
            const visible = bars.filter(b => b.slot < MAX_VISIBLE);
            const hidden = new Map(rowDays.map(d => [d, 0]));
            bars.forEach(b => {
              if (b.slot < MAX_VISIBLE) return;
              for (let c = b.startCol - 1; c < b.startCol - 1 + b.span; c++) {
                hidden.set(rowDays[c], (hidden.get(rowDays[c]) || 0) + 1);
              }
            });
            const slotsForGrid = Math.max(1, Math.min(MAX_VISIBLE, maxSlot + 1));
            return (
              <div
                key={rowIdx}
                className="grid grid-cols-7 relative"
                style={{ gridTemplateRows: rowGridTemplateRows(slotsForGrid) }}
                data-testid={`cal-week-row-${rowIdx}`}
              >
                {rowCells.map((cell, i) => {
                  const isToday = cell.dateStr === today;
                  const hiddenCount = hidden.get(cell.dateStr) || 0;
                  return (
                    <div
                      key={i}
                      style={{ gridColumn: `${i + 1}`, gridRow: `1 / -1` }}
                      className={`min-h-[140px] border-b border-r border-border last:border-r-0 p-1.5 relative ${!cell.current ? "bg-muted/10" : ""} ${isToday ? "bg-primary/5" : ""}`}
                      data-testid={cell.current ? `day-${cell.dateStr}` : undefined}
                    >
                      <div className={`text-xs font-semibold mb-1 ${isToday ? "text-primary" : cell.current ? "text-muted-foreground" : "text-muted-foreground/40"}`}>
                        {isToday ? <span className="bg-primary text-primary-foreground rounded-full px-1.5 py-0.5">{cell.day}</span> : cell.day}
                      </div>
                      {hiddenCount > 0 && (
                        <p className="absolute bottom-1 left-1.5 text-[10px] text-muted-foreground pointer-events-none">+{hiddenCount} more</p>
                      )}
                    </div>
                  );
                })}
                {visible.map(b => {
                  const e = b.event.ev;
                  const tint = TYPE_TINT[e.type] || TYPE_TINT.job;
                  const dotColor = getDotColor(e.status, e.endDate, today);
                  const showTitleParts = !b.continuesLeft;
                  return (
                    <a
                      key={`${e.id}-${rowIdx}`}
                      href={e.href || "#"}
                      className={`flex items-center gap-1 text-[10px] leading-tight px-1.5 font-medium ${tint} rounded hover:brightness-95 transition`}
                      style={{
                        gridColumn: `${b.startCol} / span ${b.span}`,
                        gridRow: 2 + b.slot,
                        height: `${BAR_HEIGHT_PX - 2}px`,
                        marginLeft: b.continuesLeft ? 0 : 3,
                        marginRight: b.continuesRight ? 0 : 3,
                        marginTop: 1,
                        position: "relative",
                        zIndex: 5,
                      }}
                      title={`${e.type === "emergency" ? "Emergency · " : e.type === "task" ? "Task · " : "Job · "}${e.ref}: ${e.title}${e.date !== e.endDate ? ` (${e.date} → ${e.endDate})` : ""}${e.vendor ? ` · 👤 ${e.vendor}` : e.type === "task" ? "" : " · Unassigned"}${e.status ? " · " + e.status.replace(/_/g, " ") : ""}`}
                      data-testid={`cal-event-${e.id}${b.isMultiDay ? "-bar" : ""}`}
                    >
                      <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: dotColor }} data-testid={`cal-dot-${e.id}`} />
                      {b.continuesLeft && <span className="opacity-60 shrink-0">‹</span>}
                      {showTitleParts && e.time && <span className="opacity-70 shrink-0">{e.time}</span>}
                      <span className="truncate flex-1">{e.title}</span>
                      {e.type === "job" && <ComplianceBadge summary={complianceByJob[e.id]} jobId={e.id} size={10} />}
                      <AssigneeChip
                        name={e.vendor}
                        kind={e.assignedKind}
                        type={e.type}
                        title={e.title}
                        dateRange={e.date === e.endDate ? e.date : `${e.date} → ${e.endDate}`}
                        status={e.status}
                        testId={`cal-assignee-${e.id}`}
                      />
                      {b.continuesRight && <span className="opacity-60 shrink-0">›</span>}
                    </a>
                  );
                })}
              </div>
            );
          })}
        </div>
        );
      })()}

      {/* WEEK VIEW */}
      {viewMode === "week" && (() => {
        const { bars, maxSlot } = computeRowBars(weekDates, barEvents);
        const MAX_VISIBLE = 14;
        const visible = bars.filter(b => b.slot < MAX_VISIBLE);
        const slotsForGrid = Math.max(1, Math.min(MAX_VISIBLE, maxSlot + 1));
        return (
        <div className="border border-border rounded-lg overflow-hidden bg-card" data-testid="calendar-week-view">
          <div className="grid grid-cols-7 border-b border-border">
            {weekDates.map((ds, i) => {
              const d = new Date(ds + "T00:00:00");
              return (
                <div key={ds} className={`px-2 py-2 text-center border-r border-border last:border-r-0 ${ds === today ? "bg-primary/10" : "bg-muted/30"}`}>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase">{DAYS[i]}</p>
                  <p className={`text-sm font-bold ${ds === today ? "text-primary" : ""}`}>{d.getDate()}</p>
                </div>
              );
            })}
          </div>
          <div
            className="grid grid-cols-7 relative"
            style={{ gridTemplateRows: rowGridTemplateRows(slotsForGrid, 0) }}
            data-testid="cal-week-row-0"
          >
            {weekDates.map((ds, i) => {
              const isToday = ds === today;
              return (
                <div
                  key={ds}
                  style={{ gridColumn: `${i + 1}`, gridRow: `1 / -1` }}
                  className={`min-h-[420px] border-r border-border last:border-r-0 ${isToday ? "bg-primary/5" : ""}`}
                  data-testid={`week-day-${ds}`}
                />
              );
            })}
            {visible.map(b => {
              const e = b.event.ev;
              const tint = TYPE_TINT[e.type] || TYPE_TINT.job;
              const dotColor = getDotColor(e.status, e.endDate, today);
              const showTitleParts = !b.continuesLeft;
              return (
                <a
                  key={e.id}
                  href={e.href || "#"}
                  className={`flex items-center gap-1 text-[11px] leading-tight px-1.5 font-medium ${tint} rounded hover:brightness-95 transition`}
                  style={{
                    gridColumn: `${b.startCol} / span ${b.span}`,
                    gridRow: 2 + b.slot,
                    height: `${BAR_HEIGHT_PX - 2}px`,
                    marginLeft: b.continuesLeft ? 0 : 3,
                    marginRight: b.continuesRight ? 0 : 3,
                    marginTop: 1,
                    position: "relative",
                    zIndex: 5,
                  }}
                  title={`${e.type === "emergency" ? "Emergency · " : e.type === "task" ? "Task · " : "Job · "}${e.ref}: ${e.title}${e.vendor ? ` · 👤 ${e.vendor}` : ""}${e.status ? " · " + e.status.replace(/_/g, " ") : ""}`}
                  data-testid={`week-event-${e.id}${b.isMultiDay ? "-bar" : ""}`}
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: dotColor }} />
                  {b.continuesLeft && <span className="opacity-60 shrink-0">‹</span>}
                  {showTitleParts && e.time && <span className="opacity-70 shrink-0">{e.time}</span>}
                  <span className="truncate flex-1">{e.title}</span>
                  {e.type === "job" && <ComplianceBadge summary={complianceByJob[e.id]} jobId={e.id} size={10} />}
                  <AssigneeChip name={e.vendor} kind={e.assignedKind} type={e.type} title={e.title} dateRange={e.date === e.endDate ? e.date : `${e.date} → ${e.endDate}`} status={e.status} testId={`week-assignee-${e.id}`} />
                  {b.continuesRight && <span className="opacity-60 shrink-0">›</span>}
                </a>
              );
            })}
          </div>
        </div>
        );
      })()}

      {/* DAY VIEW */}
      {viewMode === "day" && (() => {
        const dayEvents = (eventsByDate[anchorDate] || []).filter(e => e.position !== "middle" && e.position !== "end");
        // Combine continuing events too — show every entry whose date or span covers anchorDate
        const allDayEvents = eventsByDate[anchorDate] || [];
        const isToday = anchorDate === today;
        return (
          <div className="border border-border rounded-lg overflow-hidden bg-card" data-testid="calendar-day-view">
            <div className={`px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2 ${isToday ? "bg-primary/10" : "bg-muted/30"}`}>
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase">
                  {new Date(anchorDate + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long" })}
                </p>
                <p className={`text-xl font-bold ${isToday ? "text-primary" : ""}`}>
                  {new Date(anchorDate + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "long" })} · {allDayEvents.length} event{allDayEvents.length !== 1 ? "s" : ""}
                </p>
              </div>
              {/* Phase 284 — Day-view sub-toggle */}
              <div className="inline-flex rounded-md border border-border bg-card overflow-hidden text-[11px]" data-testid="day-mode-toggle">
                {["list", "timeline"].map(m => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setDayMode(m)}
                    className={`px-3 py-1.5 capitalize font-medium transition ${dayMode === m ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                    data-testid={`day-mode-${m}`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
            {allDayEvents.length === 0 ? (
              <div className="px-4 py-16 text-center" data-testid="day-empty">
                <CalendarBlank size={32} className="mx-auto text-muted-foreground/40 mb-2" />
                <p className="text-sm text-muted-foreground">Nothing scheduled for this day.</p>
              </div>
            ) : dayMode === "list" ? (
              <ul className="divide-y divide-border" data-testid="day-list">
                {allDayEvents.map((e, idx) => {
                  const tint = TYPE_TINT[e.type] || TYPE_TINT.job;
                  const dotColor = getDotColor(e.status, e.endDate, today);
                  const isMulti = e.date !== e.endDate;
                  return (
                    <li key={`${e.id}-${idx}`} data-testid={`day-event-${e.id}`} className="row-hover transition-colors">
                      <a href={e.href || "#"} className="flex items-start gap-3 px-4 py-3">
                        <span className={`inline-flex items-center justify-center w-[3px] self-stretch ${tint} rounded`} />
                        <span className="inline-block w-2 h-2 rounded-full mt-2 shrink-0" style={{ backgroundColor: dotColor }} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold">{e.title}</p>
                            <span className="text-[10px] font-mono text-muted-foreground">{e.ref}</span>
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground capitalize">{e.type}</span>
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            {e.time ? `${e.time}` : "All day"}
                            {isMulti && ` · spans ${e.date} → ${e.endDate}`}
                            {e.status ? ` · ${e.status.replace(/_/g, " ")}` : ""}
                          </p>
                          {e.address && (
                            <p className="text-[11px] text-muted-foreground mt-0.5">📍 {e.address}</p>
                          )}
                        </div>
                        {canComplete && completeEntity(e) && (
                          <QuickCompleteButton entity={completeEntity(e)} id={e.id} reference={e.ref} status={e.status} onDone={load} className="self-center" testId={`day-complete-${e.id}`} />
                        )}
                        <AssigneeChip name={e.vendor} kind={e.assignedKind} type={e.type} title={e.title} dateRange={e.date === e.endDate ? e.date : `${e.date} → ${e.endDate}`} status={e.status} testId={`day-assignee-${e.id}`} />
                      </a>
                    </li>
                  );
                })}
              </ul>
            ) : (
              // Phase 284 — Timeline view: 06:00-22:00 vertical hour grid
              (() => {
                const HOURS = Array.from({ length: 17 }, (_, i) => i + 6); // 06..22
                const PX_PER_HOUR = 56;
                const parseTime = (t) => {
                  if (!t || typeof t !== "string") return null;
                  const [h, m] = t.split(":").map(Number);
                  if (Number.isNaN(h)) return null;
                  return h + (m || 0) / 60;
                };
                const timedEvents = allDayEvents.filter(e => parseTime(e.time) !== null);
                const allDayItems = allDayEvents.filter(e => parseTime(e.time) === null);
                return (
                  <div data-testid="day-timeline">
                    {allDayItems.length > 0 && (
                      <div className="px-4 py-2 border-b border-border bg-muted/30">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">All-day</p>
                        <div className="flex flex-wrap gap-1.5">
                          {allDayItems.map((e, idx) => {
                            const tint = TYPE_TINT[e.type] || TYPE_TINT.job;
                            return (
                              <a key={`ad-${e.id}-${idx}`} href={e.href || "#"} className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium ${tint} hover:brightness-95 transition`} data-testid={`day-timeline-allday-${e.id}`}>
                                <span className="truncate max-w-[180px]">{e.title}</span>
                                <AssigneeChip name={e.vendor} kind={e.assignedKind} type={e.type} title={e.title} dateRange={e.date} status={e.status} testId={`day-timeline-assignee-${e.id}`} />
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div className="relative" style={{ height: `${HOURS.length * PX_PER_HOUR}px` }}>
                      {/* Hour grid */}
                      {HOURS.map((h, i) => (
                        <div key={h} className="absolute left-0 right-0 border-t border-border/60 flex" style={{ top: `${i * PX_PER_HOUR}px`, height: `${PX_PER_HOUR}px` }}>
                          <div className="w-14 px-2 py-1 text-[10px] font-mono text-muted-foreground border-r border-border/60 shrink-0">
                            {String(h).padStart(2, "0")}:00
                          </div>
                          <div className="flex-1" />
                        </div>
                      ))}
                      {/* Plot timed events */}
                      {timedEvents.map((e, idx) => {
                        const startH = parseTime(e.time);
                        // best-effort end-time guess: emergency/job often has end_time field, task uses 1hr default
                        const endTimeStr = (allDayEvents.find(x => x.id === e.id && x.endTime) || {}).endTime || e.endTime || "";
                        const endH = parseTime(endTimeStr) ?? (startH + 1);
                        const topPct = Math.max(0, startH - 6) * PX_PER_HOUR;
                        const heightPct = Math.max(28, (endH - startH) * PX_PER_HOUR);
                        const tint = TYPE_TINT[e.type] || TYPE_TINT.job;
                        const dotColor = getDotColor(e.status, e.endDate, today);
                        return (
                          <a key={`tl-${e.id}-${idx}`} href={e.href || "#"} className={`absolute left-16 right-2 rounded ${tint} hover:brightness-95 transition shadow-sm ring-1 ring-black/10 overflow-hidden`} style={{ top: `${topPct}px`, height: `${heightPct}px` }} data-testid={`day-timeline-event-${e.id}`}>
                            <div className="px-2 py-1 h-full flex flex-col">
                              <div className="flex items-center gap-1 min-h-0">
                                <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: dotColor }} />
                                <span className="text-[10px] font-mono opacity-75 shrink-0">{e.time}{endTimeStr ? ` – ${endTimeStr}` : ""}</span>
                                <span className="text-[11px] font-semibold truncate flex-1">{e.title}</span>
                                {canComplete && completeEntity(e) && (
                                  <QuickCompleteButton entity={completeEntity(e)} id={e.id} reference={e.ref} status={e.status} onDone={load} className="h-5" testId={`day-timeline-complete-${e.id}`} />
                                )}
                                <AssigneeChip name={e.vendor} kind={e.assignedKind} type={e.type} title={e.title} dateRange={e.date === e.endDate ? e.date : `${e.date} → ${e.endDate}`} status={e.status} testId={`day-timeline-assignee-${e.id}`} />
                              </div>
                              {heightPct > 44 && (
                                <p className="text-[10px] opacity-80 mt-1 truncate">{e.ref}{e.address ? ` · ${e.address}` : ""}</p>
                              )}
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                );
              })()
            )}
          </div>
        );
      })()}

      {/* Legend */}
      <div className="flex items-center gap-x-4 gap-y-1 mt-3 text-[10px] text-muted-foreground flex-wrap" data-testid="calendar-legend">
        <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-red-100 border border-red-200 dark:bg-red-900/40 dark:border-red-800" /> Emergency</span>
        <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-blue-100 border border-blue-200 dark:bg-blue-900/40 dark:border-blue-800" /> Jobs</span>
        <span className="flex items-center gap-1" data-testid="legend-contract-visits"><span className="w-3 h-2 rounded bg-teal-100 border border-teal-200 dark:bg-teal-900/40 dark:border-teal-800" /> Contract visits</span>
        <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-violet-100 border border-violet-200 dark:bg-violet-900/40 dark:border-violet-800" /> Tasks</span>
        <span className="opacity-50">·</span>
        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#1d4ed8" }} /> Scheduled</span>
        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#ea580c" }} /> In progress</span>
        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#10b981" }} /> Completed</span>
        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#dc2626" }} /> Overdue</span>
      </div>
    </div>
  );
}
